import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  HardFormFieldTypeEnum,
  HardFormService,
  IHardFormField,
  IHardFormSelectParams,
} from "@hardcode-kz/ui";
import { BaseModalComponent } from "@modals/base-modal/base-modal.component";
import {
  IGridResponse,
  IGridScreenParams,
  IGridWidget,
  IGridWidgetSettings,
  IWidgetParams,
} from "@modules/grid/models/grid-manager";
import { GridManagerService } from "@modules/grid/services/grid-manager.service";
import { FormGroup, UntypedFormGroup, Validators } from "@angular/forms";
import { UserPermissions } from "@models/user-management/user";
import { showEnter } from "@dmms/shared/animations/animation";
import { IBaseConfig } from "@models/form/base-config";
import { BaseGridWidgetConfig } from "@dmms/shared/configs/grid/base-grid-widget.config";
import { GridWidgetSettingsConfig } from "@dmms/shared/configs/grid/grid-widget-settings.config";
import { catchError, of, take } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NotificationService } from "@dmms/core/notification";
import { ConfirmationDialogService } from "@dmms/shared/ui-kit/dialogs";
import { InterfaceManagementApiService } from "@dmms/shared/data-access/interface-management/services/interface-management-api.service";
import { GridScreenConfig } from "@dmms/shared/configs/grid/grid-screen.config";
import { AdministrationEndpoints } from "@dmms/shared/data-access/administration/types/administration-endpoints";
import { IUserRole } from "@models/user-management/user-role";
import { IGridManagerRole } from "@modules/grid/models/grid-manager-role";

@Component({
  selector: "app-widget-settings-modal", // isUsed
  templateUrl: "./widget-settings-modal.component.html",
  styleUrl: "./widget-settings-modal.component.scss",
  standalone: false,
  animations: [showEnter],
})
export class WidgetSettingsModalComponent
  extends BaseModalComponent<WidgetSettingsModalComponent, undefined>
  implements OnInit, OnDestroy
{
  loading: boolean = true;
  destroyRef = inject(DestroyRef);
  gridViewPermission = [UserPermissions.AdministrationGridsView];
  gridEditPermission = [UserPermissions.AdministrationGridsEdit];

  grid: IGridResponse;
  widgets: Partial<IGridWidget>[] = [];
  selectedItem: Partial<IGridWidget> | null = null;

  baseForm: UntypedFormGroup; // базовые поля
  widgetTypeForm: UntypedFormGroup; // выбор виджета
  paramsForm: UntypedFormGroup; // для дополнительных полей с params
  screenForm: UntypedFormGroup; // для полей экрана(создание screen)
  settingsForm: UntypedFormGroup; // для настройки с settings

  reloadLoading = false;
  reloadTimeOut?: ReturnType<typeof setTimeout>;

  widgetTypeConfig: IHardFormField<IHardFormSelectParams> = {
    name: "widgetType",
    type: HardFormFieldTypeEnum.Select,
    title: "select.widget.type",
    params: {
      isGroup: true,
      options: [],
      fieldNames: ["title"],
      fieldValue: "widgetName",
    },
  };

  baseConfig: IBaseConfig = new BaseGridWidgetConfig();
  screenConfig: IBaseConfig = new GridScreenConfig();
  settingsConfig: IBaseConfig = new GridWidgetSettingsConfig();

  paramsConfig: IBaseConfig = {
    version: 1,
    fields: [],
  };

  widgetSettingsByRoles: IGridManagerRole[] = [];
  rolesIndex = 0;

  constructor(
    private readonly gridService: GridManagerService,
    private readonly apiService: InterfaceManagementApiService,
    private readonly formConstructor: HardFormService,
    private readonly notificationService: NotificationService,
    private readonly confirmationService: ConfirmationDialogService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadGrids().then();
    this.createForm();
    this.changesWidgetTypeForm();
  }

  async loadGrids() {
    const takeGridScreenFromRole = false;
    this.grid = await this.gridService.getGridScreen(takeGridScreenFromRole);
    const widgets = (this.grid?.screen?.grids || []).map((w, key) => {
      return {
        ...w,
        ...{
          key,
          widgetName: w.component + w.id,
          pageId: this.grid?.pageId,
          url: this.grid.url,
          screenId: this.grid?.screen?.id,
        },
      };
    });

    let key = this.widgets.length - 1;
    const widgetTemplates = [];

    for (const item in this.gridService.widgets) {
      key++;

      widgetTemplates.push({
        pageId: this.grid?.pageId || null,
        screenId: this.grid?.screen?.id || null,
        screenWidgetId: null,
        id: null,
        component: item,
        widgetName: item,
        title: item,
        url: this.grid?.url || this.gridService.getCurrentUrl(),
        key,
      });
    }

    this.widgets = widgets.concat(widgetTemplates);

    if (widgets?.length) {
      this.widgetTypeConfig.params.options.push({
        groupName: "widget.general",
        groupItems: widgets,
      });
    }

    if (widgetTemplates?.length) {
      this.widgetTypeConfig.params.options.push({
        groupName: "widget.template",
        groupItems: widgetTemplates,
      });
    }

    this.loading = false;
  }

  createForm() {
    this.widgetTypeForm = this.formConstructor.buildReactiveForm([
      this.widgetTypeConfig,
    ]);

    this.baseForm = this.formConstructor.buildReactiveForm(
      this.baseConfig.fields,
    );

    this.settingsForm = this.formConstructor.buildReactiveForm(
      this.settingsConfig.fields,
    );
  }

  changesWidgetTypeForm() {
    this.widgetTypeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: { widgetType: string }) => {
        this.selectedItem = this.widgets.find(
          (widget) => widget.widgetName == value.widgetType,
        );
        if (this.selectedItem) {
          this.reloadLoading = true;

          if (!this.selectedItem.screenWidgetId) {
            this.screenForm = this.formConstructor.buildReactiveForm(
              this.screenConfig.fields,
            );
          }

          this.baseForm.get("title").setValue(this.selectedItem?.title);

          this.baseForm
            .get("subTitle")
            .setValue(this.selectedItem?.params?.subTitle);

          this.baseForm.get("icon").setValue(this.selectedItem?.params?.icon);

          this.getWidgetFields(this.selectedItem);
        }
      });
  }

  getWidgetFields(item: Partial<IGridWidget>) {
    if (this.selectedItem?.settings) {
      this.gridService.buildFormControlsRecursively(
        this.selectedItem.settings,
        this.settingsForm,
        this.settingsConfig,
      );
      this.settingsForm.patchValue(this.selectedItem.settings);
    } else {
      this.settingsForm.reset();
    }

    let fields: IHardFormField<any>[] =
      this.gridService.widgets[item.component]?.configFields || [];

    this.selectedItem.configuration = fields;

    this.paramsConfig.fields = fields ?? [];

    this.paramsForm = this.formConstructor.buildReactiveForm(
      this.paramsConfig.fields,
    );

    this.gridService.buildFormControlsRecursively(
      this.selectedItem?.params,
      this.paramsForm,
      this.paramsConfig,
    );

    this.paramsForm.patchValue(this.selectedItem.params);

    this.widgetSettingsByRoles = [];
    item.params?.rolesParams?.forEach((item) => {
      this.addWidgetForRoles(this.paramsConfig, item?.params);
    });

    if (this.reloadLoading) {
      clearTimeout(this.reloadTimeOut);

      this.reloadTimeOut = setTimeout(() => {
        this.reloadLoading = false;
      }, 150);
    }
  }

  save(): void {
    this.loading = true;
    const paramsData = this.paramsForm.getRawValue();
    const baseFormData = this.baseForm.getRawValue();
    const settingsFormData: IGridWidgetSettings =
      this.settingsForm.getRawValue();
    const screenFormData: IGridScreenParams = this.screenForm?.getRawValue();

    const rolesParams = this.getRolesParams();

    const data: Partial<IGridWidget> = {
      widgetId: this.selectedItem?.id || null,
      title: baseFormData.title,
      component: this.selectedItem.component,
      screenWidgetId: this.selectedItem?.screenWidgetId || null,
      settings: { ...settingsFormData },
      params: {
        ...paramsData,
        ...{
          icon: baseFormData.icon ?? null,
          subTitle: baseFormData.subTitle ?? null,
          rolesParams,
        },
      },
      screen: screenFormData,
      pageId: this.selectedItem?.pageId || null,
      url: this.selectedItem?.url || null,
      screenId: this.selectedItem?.screenId || null,
    };

    if (data.widgetId) {
      this.editWidget(data);
    } else {
      this.reloadLoading = true;
      this.gridService.createWidgetFlow(data).subscribe((widget) => {
        if (widget?.isSuccess) {
          this.notificationService.success("grid.widget.created.successfully");
          this.screenForm.reset();
          this.selectedItem.screenId = widget.value.screenId;
          this.selectedItem.screenWidgetId = widget.value.screenWidgetId;
          this.selectedItem.id = widget.value.widgetId;
          this.loadGrids().then();
          window.location.reload();
          this.reloadLoading = false;
        } else {
          this.loading = false;
        }
      });
    }
  }

  editWidget(data: Partial<IGridWidget>) {
    this.apiService
      .editWidget(data.widgetId, data.screenWidgetId, data)
      .pipe(
        take(1),
        catchError(() =>
          of({
            isSuccess: false,
          }),
        ),
      )
      .subscribe((res) => {
        if (res?.isSuccess) {
          this.notificationService.success("grid.widget.updated.successfully");
          this.loadGrids().then();
          window.location.reload();
        } else {
          this.loading = false;
        }
      });
  }

  confirmDelete(): void {
    this.confirmationService
      .removeConfirmationDialog("grid.widget.delete.confirmation")
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.deleteWidget();
        }
      });
  }

  deleteWidget(): void {
    this.apiService
      .deleteWidget(this.selectedItem.screenWidgetId)
      .pipe(
        take(1),
        catchError(() =>
          of({
            isSuccess: false,
          }),
        ),
      )
      .subscribe((res) => {
        if (res?.isSuccess) {
          this.notificationService.success("grid.widget.deleted.successfully");
          window.location.reload();
        } else {
          this.notificationService.error(`error.occurred`);
          this.loading = false;
        }
      });
  }

  getRolesParams() {
    return this.widgetSettingsByRoles
      .filter((roleSetting) => roleSetting.paramsForm.valid)
      .map((roleSetting) => {
        return {
          params: roleSetting.paramsForm.getRawValue(),
        };
      });
  }

  addWidgetForRoles(
    paramsConfig: IBaseConfig,
    formValues?: IWidgetParams,
  ): void {
    const fields = [
      {
        name: "roles",
        title: "dicTable.roles",
        type: HardFormFieldTypeEnum.FilterSelect,
        value: [],
        params: {
          validators: [Validators.required],
          dicType: "api/v1/" + AdministrationEndpoints.UserRole,
          multiple: true,
          dataCallback: (data: IUserRole[]) => {
            return data?.filter((item) => {
              return !this.widgetSettingsByRoles.some((setting, idx) => {
                if (idx === this.rolesIndex - 1) return false;

                const roles = setting.paramsForm.get("roles")?.value;
                if (!Array.isArray(roles)) return false;

                return roles.some((role) => role?.id === item.id);
              });
            });
          },
        },
      } as IHardFormField<IHardFormSelectParams>,
      ...paramsConfig.fields,
    ];

    const paramsForm: FormGroup =
      this.formConstructor.buildReactiveForm(fields);

    if (formValues) {
      paramsForm.patchValue(formValues);
    } else {
      paramsForm.reset(this.paramsForm.getRawValue());
    }

    this.widgetSettingsByRoles.push({
      config: {
        version: 1,
        fields,
      },
      paramsForm,
    });

    if (!formValues) {
      this.rolesIndex = this.widgetSettingsByRoles.length;
    }
  }

  removeRoleSetting(): void {
    this.widgetSettingsByRoles.splice(this.rolesIndex - 1, 1);
    this.rolesIndex -= 1;
  }

  ngOnDestroy(): void {
    clearTimeout(this.reloadTimeOut);
  }
}
